import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import AuthService from 'services/AuthService'
import SignupStepService from 'services/SignupStepService'
import SkipStepService from 'services/SkipStepService'
import UserRolesEnum from '../enums/UserRolesEnum'
import UserMembershipStatusEnum from '../enums/UserMembershipStatusEnum'
import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

function evaluateRules(isLoggedIn, user, activeMember, practice, isComingFromLogin) {
    return {
        userIsLoggedIn: isLoggedIn,
        userHasPractice: !isEmpty(practice),
        userHasRole: !isEmpty(activeMember.userRole),
        userIsPhysicianOrNurseP: [UserRolesEnum.PHYSICIAN.ID, UserRolesEnum.NURSE_PRACTITIONER.ID].includes(
            get(activeMember, 'userRole.id'),
        ),
        userIsManager: get(activeMember, 'userRole.id') === UserRolesEnum.OFFICE_MANAGER.ID,
        userIsOther:
            get(user, 'userRole.id') !== UserRolesEnum.PHYSICIAN.ID &&
            get(user, 'userRole.id') !== UserRolesEnum.OFFICE_MANAGER.ID,
        userHasNpiNumber: activeMember.npiNumber,
        userHasLicenses: get(activeMember, 'medicalLicenses.length'),
        userHasSpecialities: get(activeMember, 'specialities.length'),
        userHasReferralsUserData:
            activeMember.boardCertified !== null &&
            activeMember.disciplinaryActions !== null &&
            activeMember.malpracticeClaimsLost !== null,
        userScheduleIsSet: activeMember?.isAvailabilitySet,
        facilityServiceTypes: get(practice, 'facilityServiceTypes')?.length ? true : false,
        practiceIsPhysician: get(practice, 'practiceType') === PracticeTypeEnum.PHYSICIAN,
        usersPracticeIsPhysician: get(user, 'practiceType') === PracticeTypeEnum.PHYSICIAN,
        practiceIsAncillary:
            get(practice, 'practiceType') === PracticeTypeEnum.ANCILLARY || user.practiceType === PracticeTypeEnum.ANCILLARY,
        practiceScheduleIsSet: practice?.isAvailabilitySet,
        showCongradulationsStep: SignupStepService.shouldShowSignupStep('congratulations'),
        skipRegisterPhysiciansStep: SkipStepService.shouldShowStep('registerPhysicians'),
        showPhysicianPasswordStep: SignupStepService.shouldShowSignupStep('physicianPassword'),
        isAdminLoggedIn: get(user, 'userRole.id') === UserRolesEnum.OFFICE_MANAGER.ID,
        isPracticeMembershipConfirmed: user.practiceMembershipStatus === UserMembershipStatusEnum.APPROVED,
        isMasterUser: activeMember.isMasterUser,
        isAdminRegisteringPhysician:
            get(user, 'userRole.id') === UserRolesEnum.OFFICE_MANAGER.ID &&
            get(activeMember, 'userRole.id') === UserRolesEnum.PHYSICIAN.ID,
        userCanModifyPhysicianAvailability: user.canModifyPracticeMemberAvailability,
        isPracticeCreator: practice.masterUserId === user.id,
        isPhysicianLoggedIn: get(user, 'userRole.id') === UserRolesEnum.PHYSICIAN.ID,
        isComingFromLogin,
        wasDemo: user.wasDemo,
    }
}

function generateRedirections(RULES) {
    return [
        {
            route: 'auth.signup.practiceMissing',
            rules: [
                !RULES.userIsPhysicianOrNurseP && !RULES.userIsManager,
                RULES.userIsLoggedIn,
                RULES.practiceIsPhysician || !RULES.userHasPractice,
            ],
        },
        {
            route: 'auth.signup.ancillaryService',
            rules: [
                RULES.userIsLoggedIn,
                !RULES.wasDemo,
                !RULES.practiceIsPhysician || !RULES.userHasPractice,
                !RULES.practiceIsAncillary,
                !RULES.usersPracticeIsPhysician,
            ],
        },
        {
            route: 'auth.signup.practice',
            rules: [
                RULES.userIsPhysicianOrNurseP || RULES.userIsManager || RULES.practiceIsAncillary,
                RULES.userIsLoggedIn,
                RULES.practiceIsPhysician || !RULES.userHasPractice,
            ],
        },
        {
            route: 'auth.signup.practiceMembership',
            rules: [
                RULES.userIsLoggedIn,
                RULES.userHasPractice,
                RULES.userHasRole || RULES.practiceIsAncillary,
                RULES.practiceIsPhysician || RULES.practiceIsAncillary,
                !RULES.isPracticeMembershipConfirmed || !RULES.isPracticeCreator,
            ],
        },
        {
            route: 'auth.signup.registerPhysicians',
            rules: [
                RULES.userIsLoggedIn,
                RULES.userHasPractice,
                RULES.userHasRole,
                RULES.isAdminLoggedIn,
                RULES.practiceIsPhysician,
                (RULES.isPracticeMembershipConfirmed && RULES.isPracticeCreator) || RULES.isAdminRegisteringPhysician,
            ],
        },
        {
            route: 'auth.signup.schedule',
            rules: [
                RULES.userIsLoggedIn,
                RULES.userHasPractice,
                RULES.isPracticeMembershipConfirmed,
                (RULES.userHasRole, RULES.isPhysicianLoggedIn || (RULES.practiceIsAncillary && RULES.facilityServiceTypes)) ||
                    (RULES.isAdminLoggedIn &&
                        (RULES.isAdminRegisteringPhysician ||
                            (RULES.userIsPhysicianOrNurseP && RULES.userCanModifyPhysicianAvailability))) ||
                    (RULES.userIsOther && RULES.userCanModifyPhysicianAvailability && RULES.userIsPhysicianOrNurseP),
                (RULES.practiceIsPhysician && !RULES.userScheduleIsSet) ||
                    (RULES.practiceIsAncillary && !RULES.practiceScheduleIsSet),
            ],
        },
        {
            route: 'auth.signup.proccedToDashboard',
            rules: [
                RULES.userIsLoggedIn,
                RULES.isPracticeMembershipConfirmed,
                RULES.userHasPractice &&
                    ((RULES.practiceIsAncillary && RULES.practiceScheduleIsSet) ||
                        (RULES.isPhysicianLoggedIn && RULES.userScheduleIsSet) ||
                        (RULES.skipRegisterPhysiciansStep && RULES.isAdminLoggedIn) ||
                        (!RULES.isPhysicianLoggedIn && !RULES.isAdminLoggedIn && !RULES.practiceIsAncillary)),
            ],
        },
    ]
}

export function detectStepRoute(isComingFromLogin) {
    const { route } = generateRedirections(
        evaluateRules(
            AuthService.isLoggedIn(),
            AuthService.getUser(),
            AuthService.getActiveMember() ? AuthService.getActiveMember() : AuthService.getUser(),
            AuthService.getPractice(),
            isComingFromLogin,
        ),
    )
        .reverse()
        .find((redirection) => {
            return redirection.rules.findIndex((rule) => !rule) === -1
        })
    return route
}

export default {
    handle(to, from, next) {
        AuthService.loadSession(true).then(() => {
            const isComingFromLogin = from.path === '/login'
            const route = detectStepRoute(isComingFromLogin)
            next(route !== to.name ? { name: route } : undefined)
        })
    },
}
